import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconChevronDownSmall } from '~/ui/core/icons';
import { LanguagePicker } from '~/ui/domain/language-picker/LanguagePicker';

import type { OtherLanguageWithComments } from './use-other-languages-with-comments';

type CommentLanguagesMenuProps = {
    readonly disabled?: boolean;
    readonly languages: readonly OtherLanguageWithComments[];
    readonly onSwitchLanguage: (languageId: string) => void;
};

type CommentLanguageRowProps = {
    readonly name: string;
    readonly count: number;
};

const CommentLanguageRow: FunctionComponent<CommentLanguageRowProps> = ({ name, count }) => (
    <span className="flex w-full items-center justify-between gap-l">
        <span className="truncate">{name}</span>
        <span className="shrink-0 tabular-nums text-gray-500">{count}</span>
    </span>
);

export const CommentLanguagesMenu: FunctionComponent<CommentLanguagesMenuProps> = ({
    disabled = false,
    languages,
    onSwitchLanguage,
}) => {
    const translate = useTranslate();

    return (
        <LanguagePicker
            activeText={undefined}
            disabled={disabled}
            languages={languages.map((language) => ({
                id: language.id,
                name: <CommentLanguageRow count={language.count} name={language.name} />,
            }))}
            trigger={
                <Button
                    aria-label={translate('pages.job-architecture-v2.comments.cross-language.trigger-aria')}
                    className="h-[30px] rounded-[10px] [border:1px_solid_var(--color-gray-300)]"
                    data-testid="comment-languages-selector"
                    disabled={disabled}
                    iconPosition="trailing"
                    size="sm"
                    type="button"
                    variant="secondary"
                >
                    <span>{translate('pages.job-architecture-v2.comments.cross-language.trigger')}</span>
                    <IconChevronDownSmall />
                </Button>
            }
            onLanguageChange={onSwitchLanguage}
        />
    );
};
